import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Build as DeployingIcon,
  Cancel as CancelIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';
import { RefreshIconButton } from '../components/RefreshIconButton';
import { api, endpoints } from '../services/api';
import { getDeploymentStatusMetadata } from '../utils/statusMetadata';
import { useMode } from '../contexts/ModeContext';
import { scheduleDeferredWork } from '../utils/deferredWork';

type PostDeployHealthJob = {
  deployment_id: string;
  status?: string;
  started_at_ms?: number;
  deadline_at_ms?: number;
  last_poll_at_ms?: number;
  devices_total?: number;
  degraded_total?: number;
  degraded_device_ids?: string[];
};

interface ClarityDeployment {
  deployment_id: string;
  config_type: string;
  org_id: string;
  site_id: string;
  edge_device_id: string;
  camera_id?: string | null;
  camera_ids?: string[] | null;
  camera_count?: number | null;
  bundle_version: number;
  bundle_hash: string;
  status: string;
  created_at: string;
  published_at?: string | null;
  acked_at?: string | null;
  nacked_at?: string | null;
  timeout_at?: string | null;
  initiated_by?: string | null;
  error?: any;
  payload?: any;
}

const PAGE_TIMEOUT_MS = 10000;

function withTimeout<T>(promise: Promise<T>, label: string, timeoutMs: number = PAGE_TIMEOUT_MS): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

const hasScheduleWindows = (windows: any): boolean => {
  if (!windows || typeof windows !== 'object') return false;
  return Object.values(windows).some((list) => Array.isArray(list) && list.length > 0);
};

const isTimeBasedSchedule = (schedule: any): boolean => {
  if (!schedule) return false;
  if (schedule.enabled === false) return false;
  const hasLegacy =
    (Array.isArray(schedule.days) && schedule.days.length > 0) ||
    Boolean(schedule.start_time) ||
    Boolean(schedule.end_time);
  const hasExceptions = Array.isArray(schedule.exceptions) && schedule.exceptions.length > 0;
  return schedule.enabled === true || hasScheduleWindows(schedule.windows) || hasLegacy || hasExceptions;
};

const deploymentUsesSchedule = (deployment: ClarityDeployment): boolean => {
  if (deployment.config_type !== 'policies') return false;
  const payload = deployment.payload || {};
  const policies = Array.isArray(payload.policies) ? payload.policies : [];
  return policies.some((pol: any) => isTimeBasedSchedule(pol?.schedule));
};

const _titleCase = (s: string): string => {
  const raw = String(s || '').trim();
  if (!raw) return '';
  return raw
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const Deployments: React.FC = () => {
  const { mode } = useMode();
  const [clarityDeployments, setClarityDeployments] = useState<ClarityDeployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClarityDeployment, setSelectedClarityDeployment] = useState<ClarityDeployment | null>(null);
  const [postDeployHealthJob, setPostDeployHealthJob] = useState<PostDeployHealthJob | null>(null);
  const [postDeployHealthLoading, setPostDeployHealthLoading] = useState(false);
  const [postDeployHealthError, setPostDeployHealthError] = useState<string | null>(null);
  const [showFailed, setShowFailed] = useState(false);
  const [clockStatusByDevice, setClockStatusByDevice] = useState<Record<string, boolean | null>>({});
  const deploymentsClockGenRef = useRef(0);

  useEffect(() => {
    refreshClarityDeployments();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only
  }, []);

  useEffect(() => {
    const depId = String(selectedClarityDeployment?.deployment_id || '').trim();
    if (!depId) {
      setPostDeployHealthJob(null);
      setPostDeployHealthError(null);
      setPostDeployHealthLoading(false);
      return;
    }
    let cancelled = false;
    let interval: any = null;
    const pollEveryMs = 15000;

    const _shouldKeepPolling = (job: PostDeployHealthJob | null): boolean => {
      const st = String(job?.status || '').toLowerCase();
      if (!st) return false;
      return st === 'scheduled' || st === 'running' || st === 'degraded';
    };

    const fetchOnce = async () => {
      setPostDeployHealthLoading(true);
      setPostDeployHealthError(null);
      try {
        const res = await api.get('/api/v1/deployments/clarity/post_deploy_health', { params: { deployment_id: depId } });
        const data = res?.data || {};
        if (cancelled) return;
        if (data?.ok && data?.job) {
          const job = data.job as PostDeployHealthJob;
          setPostDeployHealthJob(job);
          if (!_shouldKeepPolling(job) && interval) {
            clearInterval(interval);
            interval = null;
          }
        } else {
          setPostDeployHealthJob(null);
          setPostDeployHealthError(data?.error || 'not_found');
          if (interval) {
            clearInterval(interval);
            interval = null;
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        setPostDeployHealthJob(null);
        setPostDeployHealthError(err?.response?.data?.error || err?.message || 'failed');
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      } finally {
        if (!cancelled) setPostDeployHealthLoading(false);
      }
    };

    // Fetch immediately and then poll while running.
    void fetchOnce();
    interval = setInterval(fetchOnce, pollEveryMs);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [selectedClarityDeployment?.deployment_id]);

  useEffect(() => {
    const deviceIds = Array.from(new Set((clarityDeployments || []).map((d) => d.edge_device_id).filter(Boolean)));
    if (deviceIds.length === 0) return;
    const gen = ++deploymentsClockGenRef.current;
    scheduleDeferredWork(() => {
      if (gen !== deploymentsClockGenRef.current) return;
      void (async () => {
        const updates: Record<string, boolean | null> = {};
        await Promise.all(
          deviceIds.map(async (deviceId) => {
            try {
              const resp = await api.get(`/api/v1/pipeline/devices/${deviceId}/edge/health`);
              const unsynced = resp?.data?.clock?.clock_unsynced;
              updates[deviceId] = typeof unsynced === 'boolean' ? unsynced : null;
            } catch {
              updates[deviceId] = null;
            }
          }),
        );
        if (gen !== deploymentsClockGenRef.current) return;
        setClockStatusByDevice((prev) => ({ ...prev, ...updates }));
      })();
    });
  }, [clarityDeployments]);

  const refreshClarityDeployments = async () => {
    try {
      setLoading(true);
      // Include payload so we can derive camera context for older policy deployments (no DB migration needed).
      const module_scope = mode === 'ocr' ? 'ocr' : 'warehouse';
      const resp = await withTimeout(
        api.get(endpoints.deployments.clarity.list, {
          params: { limit: 50, offset: 0, include_payload: true, module_scope },
          timeout: PAGE_TIMEOUT_MS,
        }),
        'Deployments'
      );
      setClarityDeployments(resp.data.deployments || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err?.message || 'Failed to fetch Clarity deployments');
    } finally {
      setLoading(false);
    }
  };

  // F-30: Use standardized status metadata (clarityStatusMeta removed, using metadata directly)
  // Legacy icon mapping for backward compatibility
  const statusIcons: Record<string, React.ReactElement> = {
    deploying: <DeployingIcon fontSize="small" />,
    active: <SuccessIcon fontSize="small" />,
    failed: <ErrorIcon fontSize="small" />,
    failed_timeout: <PendingIcon fontSize="small" />,
    rollback: <CancelIcon fontSize="small" />,
  };

  const shortHash = (h?: string) => {
    if (!h) return '';
    return h.length > 12 ? `${h.slice(0, 12)}…` : h;
  };

  const deriveCameraDisplay = (d: ClarityDeployment): string => {
    // 0) UX: if a deploy was triggered from "deploy this policy", prefer the requested camera context.
    // This avoids confusing "Multiple" when the operator was editing a single policy/camera.
    const p0 = d.payload || {};
    const requestedCameraId = p0?.requested_camera_id;
    if (typeof requestedCameraId === 'string' && requestedCameraId && requestedCameraId !== '*') return requestedCameraId;

    // 1) Preferred: column provided by API (payload->>'camera_id' or populated during publish)
    const cid = d.camera_id;
    if (cid === '*') return 'Multiple';
    if (cid && cid !== '—') return cid;

    // 2) Next: payload-provided helpers
    const p = d.payload || {};
    const pCameraId = p?.camera_id;
    if (pCameraId === '*') {
      const n = Number(p?.camera_count || 0);
      return n > 0 ? `Multiple (${n})` : 'Multiple';
    }
    if (typeof pCameraId === 'string' && pCameraId) return pCameraId;

    const pCameraIds = p?.camera_ids;
    if (Array.isArray(pCameraIds) && pCameraIds.length) {
      return pCameraIds.length === 1 ? String(pCameraIds[0]) : `Multiple (${pCameraIds.length})`;
    }

    // 3) Fallback: derive from policies[] target_cameras
    const policies = p?.policies;
    if (Array.isArray(policies) && policies.length) {
      const set = new Set<string>();
      for (const pol of policies) {
        const tc = (pol as any)?.target_cameras;
        if (Array.isArray(tc)) {
          for (const x of tc) if (x) set.add(String(x));
        }
      }
      const ids = Array.from(set.values());
      if (ids.length === 1) return ids[0];
      if (ids.length > 1) return `Multiple (${ids.length})`;
    }

    return '—';
  };

  const derivePolicyDisplay = (d: ClarityDeployment): string => {
    if (!d) return '—';
    const p = d.payload || {};

    // Policy deployments: show a clear policy identity
    if (d.config_type === 'policies') {
      const policies = Array.isArray(p.policies) ? p.policies : [];
      if (!policies.length) return '—';

      const items = policies.map((pol: any) => {
        const name = String(pol?.name || pol?.policy_name || '').trim();
        const pid = String(pol?.policy_id || pol?.id || '').trim();
        const typ = String(pol?.policy_type || pol?.type || '').trim();
        const label = name || pid || '';
        const typeLabel = typ ? _titleCase(typ) : '';
        return typeLabel && label ? `${typeLabel}: ${label}` : (label || typeLabel);
      }).filter(Boolean) as string[];

      if (items.length === 1) return items[0];

      // Multi-policy summary: show count + unique types
      const types = Array.from(
        new Set(
          policies
            .map((pol: any) => String(pol?.policy_type || pol?.type || '').trim())
            .filter(Boolean)
        )
      );
      const typeSummary = types.length ? ` (${types.map((t) => _titleCase(String(t))).join(', ')})` : '';
      return `${items.length} policies${typeSummary}`;
    }

    // Zones deployment: show count if present
    if (d.config_type === 'zones') {
      const zones = Array.isArray(p.zones) ? p.zones : (Array.isArray(p.zone_ids) ? p.zone_ids : []);
      const n = zones.length;
      if (n === 1) return '1 zone';
      if (n > 1) return `${n} zones`;
      return 'zones';
    }

    return '—';
  };

  const handleViewClarityDetails = async (deployment: ClarityDeployment) => {
    try {
      const resp = await api.get(endpoints.deployments.clarity.get(deployment.deployment_id));
      setSelectedClarityDeployment(resp.data.deployment || deployment);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch Clarity deployment details');
      setSelectedClarityDeployment(deployment);
    }
  };

  const visibleClarityDeployments = (clarityDeployments || []).filter((d) => {
    if (showFailed) return true;
    const st = String(d.status || '').toLowerCase();
    return st !== 'failed' && st !== 'failed_timeout';
  });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Deployments
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title="Open documentation (Deployments)">
            <IconButton
              size="small"
              component="a"
              href="/docs/user-guide/configuration/deployments/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DocsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <FormControlLabel
            control={<Switch checked={showFailed} onChange={(e) => setShowFailed(e.target.checked)} />}
            label={showFailed ? 'Show failed' : 'Hide failed'}
          />
          <RefreshIconButton onClick={refreshClarityDeployments} size="small" title="Refresh deployments" />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Deployment Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🚀 Total Deployments
              </Typography>
              <Typography variant="h3" color="primary">
                {visibleClarityDeployments.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ✅ Successful
              </Typography>
              <Typography variant="h3" color="success.main">
                {visibleClarityDeployments.filter((d) => d.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🔄 Active
              </Typography>
              <Typography variant="h3" color="info.main">
                {visibleClarityDeployments.filter((d) => d.status === 'deploying').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ❌ Failed
              </Typography>
              <Typography variant="h3" color="error.main">
                {visibleClarityDeployments.filter((d) => ['failed', 'failed_timeout'].includes(d.status)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Deployments Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Deployment</TableCell>
                <TableCell>Config</TableCell>
                <TableCell>Policy</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Camera</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Bundle</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>ACK/NACK</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visibleClarityDeployments.length === 0 ? (
                [0, 1, 2].map((i) => (
                  <TableRow key={i}>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : visibleClarityDeployments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="textSecondary">
                      {clarityDeployments.length > 0 && !showFailed
                        ? 'All deployments are currently failed/timeout and hidden. Toggle “Show failed” to view them.'
                        : 'No Clarity deployments yet. Deploy a policy/zones bundle, then click Refresh.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleClarityDeployments.map((d) => (
                  <TableRow key={d.deployment_id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{d.deployment_id}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {d.org_id}/{d.site_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={d.config_type} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 260 }} noWrap title={derivePolicyDisplay(d)}>
                        {derivePolicyDisplay(d)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{d.edge_device_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{deriveCameraDisplay(d)}</Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // F-30: Use standardized status metadata
                        const statusMeta = getDeploymentStatusMetadata(d.status);
                        const showClockWarn = deploymentUsesSchedule(d) && clockStatusByDevice[d.edge_device_id] === true;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              icon={statusIcons[d.status]}
                              label={statusMeta.label}
                              color={statusMeta.color}
                              size="small"
                            />
                            {showClockWarn && (
                              <Chip label="Clock unsynced" color="warning" size="small" variant="outlined" />
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        v{d.bundle_version} · {shortHash(d.bundle_hash)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {d.published_at ? new Date(d.published_at).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {d.acked_at
                          ? `ACK ${new Date(d.acked_at).toLocaleTimeString()}`
                          : d.nacked_at
                            ? `NACK ${new Date(d.nacked_at).toLocaleTimeString()}`
                            : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Deployment Details" arrow>
                        <IconButton size="small" onClick={() => handleViewClarityDetails(d)} color="info">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Clarity Deployment Details Dialog */}
      <Dialog open={!!selectedClarityDeployment} onClose={() => setSelectedClarityDeployment(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Clarity Deployment Details: {selectedClarityDeployment?.deployment_id}
        </DialogTitle>
        <DialogContent>
          {selectedClarityDeployment && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📦 Correlation
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Status:</strong> {selectedClarityDeployment.status}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Config:</strong> {selectedClarityDeployment.config_type}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Device:</strong> {selectedClarityDeployment.edge_device_id}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Camera:</strong> {deriveCameraDisplay(selectedClarityDeployment)}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Policy:</strong> {derivePolicyDisplay(selectedClarityDeployment)}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Bundle:</strong> v{selectedClarityDeployment.bundle_version} · {selectedClarityDeployment.bundle_hash}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      🕒 Timeline
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Created:</strong> {new Date(selectedClarityDeployment.created_at).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Published:</strong> {selectedClarityDeployment.published_at ? new Date(selectedClarityDeployment.published_at).toLocaleString() : '—'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>ACKed:</strong> {selectedClarityDeployment.acked_at ? new Date(selectedClarityDeployment.acked_at).toLocaleString() : '—'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>NACKed:</strong> {selectedClarityDeployment.nacked_at ? new Date(selectedClarityDeployment.nacked_at).toLocaleString() : '—'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Timeout:</strong> {selectedClarityDeployment.timeout_at ? new Date(selectedClarityDeployment.timeout_at).toLocaleString() : '—'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      🩺 Post-deploy health
                    </Typography>
                    {postDeployHealthLoading ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading…
                        </Typography>
                      </Box>
                    ) : postDeployHealthJob ? (
                      <>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`status: ${postDeployHealthJob.status || 'unknown'}`}
                            color={
                              String(postDeployHealthJob.status || '').includes('degraded')
                                ? 'warning'
                                : String(postDeployHealthJob.status || '').includes('completed')
                                  ? 'success'
                                  : 'default'
                            }
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`degraded: ${postDeployHealthJob.degraded_total ?? 0}/${postDeployHealthJob.devices_total ?? '—'}`}
                            color={(postDeployHealthJob.degraded_total || 0) > 0 ? 'warning' : 'success'}
                          />
                          {postDeployHealthJob.last_poll_at_ms ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`last poll: ${new Date(postDeployHealthJob.last_poll_at_ms).toLocaleTimeString()}`}
                            />
                          ) : null}
                        </Box>
                        {Array.isArray(postDeployHealthJob.degraded_device_ids) && postDeployHealthJob.degraded_device_ids.length > 0 ? (
                          <Alert severity="warning">
                            Degraded devices: {postDeployHealthJob.degraded_device_ids.slice(0, 8).join(', ')}
                            {postDeployHealthJob.degraded_device_ids.length > 8 ? ' …' : ''}
                          </Alert>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No degraded devices recorded for this deployment.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {postDeployHealthError ? `No poller status: ${postDeployHealthError}` : 'No poller status.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {(selectedClarityDeployment.error || selectedClarityDeployment.payload) && (
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Raw payload / error</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12, m: 0 }}>
                        {JSON.stringify(
                          { error: selectedClarityDeployment.error, payload: selectedClarityDeployment.payload },
                          null,
                          2
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedClarityDeployment(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Deployments;
